import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, Users, Ticket, ChevronRight, Star, Loader2 } from 'lucide-react';
import { formatPrice } from '../../lib/utils';
import { api } from '../../lib/api';

interface TicketType {
  id: string;
  name: string;
  price: number;
  description?: string;
}

interface Event {
  id: string;
  name: string;
  slug: string;
  category: string;
  description?: string;
  event_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  location: string;
  capacity: number;
  spots_left: number;
  image_id?: string;
  featured: number;
  status: string;
  ticketTypes: TicketType[];
}

// Default images by category
const DEFAULT_IMAGES: Record<string, string> = {
  'V8 Supercars': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
  'Drag Racing': 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=600&q=80',
  'Motorcycle': 'https://images.unsplash.com/photo-1558980664-769d59546b3d?w=600&q=80',
  'Drifting': 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80',
  'Track Day': 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80',
  'Driver Training': 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&q=80',
  'Corporate': 'https://images.unsplash.com/photo-1493238792000-8113da705763?w=600&q=80',
  'Other': 'https://images.unsplash.com/photo-1518830246650-b54e3b0de5e0?w=600&q=80',
};

export function TicketsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'featured'>('all');

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/public/events');
      if (res.success) {
        // Filter out cancelled events and sort by date
        const activeEvents = res.data
          .filter((e: Event) => e.status !== 'cancelled')
          .sort((a: Event, b: Event) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());
        setEvents(activeEvents);
      } else {
        setError('Failed to load events');
      }
    } catch (err) {
      console.error('Failed to load events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }

  const featuredEvents = events.filter(e => e.featured === 1);
  const displayEvents = filter === 'featured' ? featuredEvents : events;

  function getEventImage(event: Event) {
    return event.image_id || DEFAULT_IMAGES[event.category] || DEFAULT_IMAGES.Other;
  }

  function formatEventDate(event: Event) {
    const startDate = new Date(event.event_date);
    const formatted = startDate.toLocaleDateString('en-AU', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    if (event.end_date && event.end_date !== event.event_date) {
      const endDate = new Date(event.end_date);
      return `${formatted} - ${endDate.toLocaleDateString('en-AU', { day: 'numeric' })}`;
    }
    return formatted;
  }

  function getAvailabilityStatus(event: Event) {
    const percentLeft = (event.spots_left / event.capacity) * 100;
    if (event.status === 'sold-out' || event.spots_left === 0) {
      return { text: 'Sold Out', color: 'text-red-600' };
    }
    if (percentLeft <= 10) {
      return { text: 'Almost Sold Out', color: 'text-orange-600' };
    }
    if (percentLeft <= 25) {
      return { text: 'Selling Fast', color: 'text-yellow-600' };
    }
    return { text: `${event.spots_left.toLocaleString()} spots left`, color: 'text-gray-600' };
  }

  if (loading) {
    return (
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-red-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Event Tickets</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Purchase tickets for upcoming races, track days, and special events at DriveIT NQ.
          </p>
        </div>

        {/* Filter Tabs */}
        {featuredEvents.length > 0 && (
          <div className="flex justify-center gap-2 mb-8">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Events ({events.length})
            </button>
            <button
              onClick={() => setFilter('featured')}
              className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-1 ${
                filter === 'featured'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Star size={16} />
              Featured ({featuredEvents.length})
            </button>
          </div>
        )}

        {error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadEvents} className="btn btn-primary">
              Try Again
            </button>
          </div>
        ) : displayEvents.length === 0 ? (
          <div className="text-center py-12">
            <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Events Available</h3>
            <p className="text-gray-600">Check back soon for upcoming events!</p>
          </div>
        ) : (
          <>
            {/* Featured Events (Large Cards) */}
            {filter === 'all' && featuredEvents.length > 0 && (
              <section className="mb-16">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Events</h2>
                <div className="grid lg:grid-cols-2 gap-8">
                  {featuredEvents.map((event) => {
                    const availability = getAvailabilityStatus(event);
                    return (
                      <div key={event.id} className="card overflow-hidden group">
                        <div className="relative h-64">
                          <img
                            src={getEventImage(event)}
                            alt={event.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className="absolute top-4 left-4 flex gap-2">
                            <span className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded-full flex items-center gap-1">
                              <Star size={14} className="fill-white" />
                              Featured
                            </span>
                          </div>
                          <div className="absolute bottom-4 left-4 right-4 text-white">
                            <p className="text-sm text-red-300 mb-1">{event.category}</p>
                            <h3 className="text-2xl font-bold">{event.name}</h3>
                          </div>
                        </div>

                        <div className="p-6">
                          {event.description && (
                            <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>
                          )}

                          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                            <div className="flex items-center text-gray-600">
                              <Calendar size={16} className="mr-2 text-gray-400" />
                              {formatEventDate(event)}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Clock size={16} className="mr-2 text-gray-400" />
                              {event.start_time || '09:00'}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <MapPin size={16} className="mr-2 text-gray-400" />
                              {event.location}
                            </div>
                            <div className={`flex items-center ${availability.color}`}>
                              <Users size={16} className="mr-2" />
                              {availability.text}
                            </div>
                          </div>

                          <div className="border-t border-gray-200 pt-4">
                            <p className="text-sm text-gray-500 mb-3">Ticket options:</p>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {event.ticketTypes.slice(0, 3).map((ticket) => (
                                <span key={ticket.id} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                                  {ticket.name}: <strong>{formatPrice(ticket.price)}</strong>
                                </span>
                              ))}
                              {event.ticketTypes.length > 3 && (
                                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-500">
                                  +{event.ticketTypes.length - 3} more
                                </span>
                              )}
                            </div>
                            <Link
                              to={`/tickets/${event.slug}`}
                              className={`btn w-full justify-center ${
                                event.spots_left === 0 ? 'btn-secondary opacity-50 cursor-not-allowed' : 'btn-primary'
                              }`}
                              onClick={(e) => event.spots_left === 0 && e.preventDefault()}
                            >
                              <Ticket size={18} className="mr-2" />
                              {event.spots_left === 0 ? 'Sold Out' : 'Get Tickets'}
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Other Events (List View) */}
            <section>
              {filter === 'all' && featuredEvents.length > 0 && (
                <h2 className="text-2xl font-bold text-gray-900 mb-6">More Events</h2>
              )}
              <div className="space-y-4">
                {(filter === 'all' ? events.filter(e => e.featured !== 1) : displayEvents).map((event) => {
                  const availability = getAvailabilityStatus(event);
                  const minPrice = event.ticketTypes.length > 0
                    ? Math.min(...event.ticketTypes.map(t => t.price))
                    : 0;

                  return (
                    <div key={event.id} className="card p-4 flex items-center gap-6 hover:shadow-lg transition-shadow">
                      <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                        <img
                          src={getEventImage(event)}
                          alt={event.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm text-red-600 font-medium">{event.category}</p>
                          {event.featured === 1 && (
                            <Star size={14} className="text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 truncate">{event.name}</h3>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            {formatEventDate(event)}
                          </span>
                          <span className="flex items-center">
                            <MapPin size={14} className="mr-1" />
                            {event.location}
                          </span>
                          <span className={`flex items-center ${availability.color}`}>
                            <Users size={14} className="mr-1" />
                            {availability.text}
                          </span>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-sm text-gray-500">From</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatPrice(minPrice)}
                        </p>
                        <Link
                          to={`/tickets/${event.slug}`}
                          className={`mt-2 inline-flex items-center text-sm font-medium ${
                            event.spots_left === 0
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 hover:text-red-700'
                          }`}
                          onClick={(e) => event.spots_left === 0 && e.preventDefault()}
                        >
                          {event.spots_left === 0 ? 'Sold Out' : 'View'}
                          {event.spots_left > 0 && <ChevronRight size={16} />}
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {/* Info Box */}
        <div className="mt-12 bg-gray-50 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Ticket Information</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Purchasing Tickets</h3>
              <ul className="space-y-1 text-sm">
                <li>Tickets are non-refundable but may be transferred</li>
                <li>Children under 12 are free with a paying adult</li>
                <li>Group discounts available for 10+ tickets</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Event Day</h3>
              <ul className="space-y-1 text-sm">
                <li>Gates open 1 hour before event start</li>
                <li>Bring your e-ticket or confirmation email</li>
                <li>Food and beverages available on-site</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
